<template>
  <div>
    <v-row dense row class="mb-3">
      <v-col class="col-12">
        <v-menu :close-on-content-click="true">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              color="#607d8b"
              dark
              depressed
              small
              class="hyp-sort-btn"
            >
              <v-icon left dark size="12" class="mr-2"
                >fas fa-sort-alpha-down</v-icon
              >
              Order By {{ sortSelectedTitle }}
              <v-icon right dark size="12" class="ml-2"
                >fas fa-angle-down</v-icon
              >
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item
                v-for="(type, $index) in sortItems"
                :key="$index"
                @click="handleChangeSort(type)"
              >
                <v-list-item-title>{{ type.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <v-row dense row class="mb-3">
      <v-col class="col-12">
        <v-text-field
          v-model="search"
          :disabled="loading"
          placeholder="Search content"
          class="hyp-search"
          @keyup="handleSearchKeyup"
        >
          <template v-slot:append>
            <v-icon
              v-if="!loading"
              :size="18"
              class="mt-1"
              @click="handleSearchClick"
              >{{ searchIcon }}</v-icon
            >
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="#0000008a"
              :size="18"
            ></v-progress-circular>
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <v-row dense row class="mb-3">
      <v-col class="col-12">
        <DateRangePicker
          :refresh="refresh"
          :start="dateStart"
          :end="dateEnd"
          size="small"
          @change="handleChangeDate"
        ></DateRangePicker>
      </v-col>
    </v-row>

    <v-row v-if="social || hasVerifiedEvents" row class="mb-4">
      <v-col class="col-12">
        <v-menu :close-on-content-click="true">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              color="#607d8b"
              dark
              depressed
              small
              class="hyp-source-btn"
            >
              {{ sourceSelectedTitle }}
              <v-icon right dark size="12" class="ml-2"
                >fas fa-angle-down</v-icon
              >
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item
                v-for="(type, $index) in sourceItems"
                :key="$index"
                :disabled="type.disabled"
                @click="handleChangeSource(type)"
              >
                <v-list-item-title>{{ type.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <v-row dense row class="mb-4">
      <v-col class="col-12">
        <v-menu :close-on-content-click="true">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              :color="labelColorSelectedColor"
              dark
              depressed
              small
              class="hyp-label-color-btn"
            >
              <v-icon left dark size="12" class="mr-2">fas fa-tag</v-icon>
              {{ labelColorSelectedTitle }}
              <v-icon right dark size="12" class="ml-2"
                >fas fa-angle-down</v-icon
              >
            </v-btn>
            <v-icon
              @click="handleOpenCustomizeLabelsClick"
              :size="14"
              class="ml-2"
              title="Customize labels"
              >fas fa-pencil-alt</v-icon
            >
          </template>
          <v-card>
            <v-list>
              <v-list-item
                v-for="(color, $index) in labelColors"
                :key="$index"
                @click="handleChangeLabelColor(color)"
              >
                <v-list-item-action>
                  <v-icon class="ml-2" :color="color.value">fas fa-tag</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title v-text="color.name"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <v-row dense row class="mb-3">
      <v-col class="col-12">
        <TopicsFilterBar
          :show-totals="false"
          :shown="eventTypesShown"
          :selected="eventTypesSelected"
          :hide-all-info="true"
          :stacked="true"
          @click="handleTopicsFilterClick"
        ></TopicsFilterBar>
      </v-col>
    </v-row>

    <v-row dense row class="mb-3">
      <v-col class="col-12">
        <RiskCategoriesPicker
          v-if="hasAnalysis"
          :reset="'false'"
          :risk-categories-filter-counts="riskCategoriesFilterCounts"
          :selected-risk-categories="riskCategoriesSelected"
          @change="handleChangeRiskCategories"
        ></RiskCategoriesPicker>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogLabels" :max-width="320">
      <v-card>
        <v-card-title class="headline"> Customize Labels </v-card-title>
        <v-card-text class="py-0">
          <v-list>
            <v-list-item v-for="(label, $index) in labelNames" :key="$index">
              <v-list-item-action>
                <v-icon class="ml-2" :color="label.value">fas fa-tag</v-icon>
              </v-list-item-action>

              <v-list-item-content>
                <v-text-field
                  v-model="label.name"
                  @keyup="handleCustomizeLabelKeyup"
                  class="hyp-label-textbox"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text @click="handleCustomizeLabelsCloseClick">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashOrderBy from "lodash/orderBy";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashDebounce from "lodash/debounce";

import { mapGetters } from "vuex";

import { GET_PREFERENCES, SAVE_PREFERENCES } from "@/store/actions.type";
import { MODULE_PREFIX_USER } from "@/store";

import { getPrimaryTopics } from "@/services/events/topics.service";
import { LABEL_COLORS } from "@/common/constants/collaboration";
import { getSources } from "@/services/alerts/alerts.service";
import {
  hasTwitterAccess,
  hasEssentialsAccess,
} from "@/services/user/user.service";
import {
  upperCaseFirstLetter,
  sortArrayByPriority,
} from "@/common/basic.utilities";
import { isFeatureUnlocked } from "@/common/featureLock.js";

const EMITTED_ACTIONS = {
  CHANGE_SORT: "change-sort",
  CHANGE_SEARCH: "change-search",
  CHANGE_DATE: "change-date",
  CHANGE_SOURCE: "change-source",
  CHANGE_LABEL_COLOR: "change-label-color",
  CHANGE_TOPICS_FILTER: "change-topics-filter",
  CHANGE_RISK_CATEGORIES_FILTER: "change-risk-categories",
};

export default {
  name: "CollaborationContentFilters",
  components: {
    DateRangePicker: () => import("@/components/common/DateRangePicker"),
    TopicsFilterBar: () => import("@/components/events/TopicsFilterBar"),
    RiskCategoriesPicker: () =>
      import("@/components/riskCategories/RiskCategoriesPicker"),
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    refresh: {
      type: String,
    },
    searchMarker: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const hasEssentials = hasEssentialsAccess();
    const sources = getSources();

    let itemSources = [];
    const NEWS = lodashFind(sources, ["id", "news-all"]);
    const SOCIAL = hasTwitterAccess()
      ? lodashFind(sources, ["id", "twitter"])
      : null;
    const VERIFIED_EVENTS = isFeatureUnlocked("alert-verified-events")
      ? lodashFind(sources, ["id", "verified-event"])
      : null;
    const ANALYSIS = isFeatureUnlocked("livesearch-filter-analysis")
      ? { id: "analysis", display: "ANALYSIS" }
      : null;

    const NEWS_DISPLAY =
      NEWS && NEWS.display ? upperCaseFirstLetter(NEWS.display) : null;
    const SOCIAL_DISPLAY =
      SOCIAL && SOCIAL.display ? upperCaseFirstLetter(SOCIAL.display) : null;
    const ANALYSIS_DISPLAY =
      ANALYSIS && ANALYSIS.display
        ? upperCaseFirstLetter(ANALYSIS.display)
        : null;

    // allSources respresents the "All" selection option
    // it needs to programatically build its 'value' attribute to reflect
    // the sources available to the user based on permissions.
    let allSources = null;
    itemSources.push({
      value: NEWS.id,
      text: `Only ${NEWS_DISPLAY}`,
      disabled: hasEssentials,
    });

    if (!hasEssentials) {
      allSources = {
        value: `${NEWS.id}`,
        text: `All`,
      };
    }

    if (SOCIAL) {
      itemSources.push({
        value: SOCIAL.id,
        text: `Only ${SOCIAL_DISPLAY}`,
        disabled: hasEssentials,
      });
      if (!hasEssentials && lodashGet(allSources, "value")) {
        allSources.value = allSources.value + `,${SOCIAL.id}`;
      }
    }

    if (VERIFIED_EVENTS) {
      itemSources.push({
        value: "maritime",
        text: `Only Maritime`,
        disabled: false,
      });
      itemSources.push({
        value: "political",
        text: `Only Security`,
        disabled: false,
      });

      if (lodashGet(allSources, "value")) {
        allSources.value = allSources.value + `,maritime,political`;
      } else {
        allSources = {
          value: `maritime,political`,
          text: `All`,
        };
      }
    }

    if (ANALYSIS) {
      itemSources.push({
        value: ANALYSIS.id,
        text: `Only ${ANALYSIS_DISPLAY}`,
        disabled: false,
      });
      if (lodashGet(allSources, "value")) {
        allSources.value = allSources.value + `,${ANALYSIS.id}`;
      } else {
        allSources = {
          value: `${ANALYSIS.id}`,
          text: `All`,
        };
      }
    }
    if (allSources) {
      itemSources.push(allSources);
    }

    itemSources = sortArrayByPriority(itemSources, "text", [
      "All",
      "Only News",
      "Only Social",
      "Only Maritime",
      "Only Security",
      "Only Analysis",
    ]);

    const PRIMARY_TOPICS = getPrimaryTopics();
    const SORTED_TOPICS = lodashOrderBy(PRIMARY_TOPICS, ["order"], ["asc"]);
    let types = [];
    SORTED_TOPICS.forEach((topic) => {
      if (this.featureUnlocked(`event-${topic.type}`)) {
        types.push(topic.type);
      }
    });

    const SORT_ITEMS = [
      { name: "Article Date", value: "datePublished" },
      { name: "Saved Date", value: "dateAdded" },
      { name: "Event Type", value: "topics" },
    ];

    this.$emit(EMITTED_ACTIONS.CHANGE_SOURCE, allSources.value);

    return {
      news: NEWS,
      social: SOCIAL,
      hasVerifiedEvents: VERIFIED_EVENTS,
      hasAnalysis: ANALYSIS,
      search: null,
      dateStart: null,
      dateEnd: null,
      selectedSources: allSources.value,
      possibleSources: sources,
      itemSources: itemSources,
      eventTypes: types,
      eventTypesSelected: [],
      riskCategoriesSelected: "",
      riskCategoriesFilterCounts: {},
      labelColorSelected: null,
      labelNames: lodashCloneDeep(LABEL_COLORS),
      sortItems: SORT_ITEMS,
      sortSelected: null,
      searchIcon: this.searchMarker ? "fas fa-times" : "fas fa-search",
      dialogLabels: false,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    sourceItems() {
      if (!this.social && !this.hasVerifiedEvents && !this.hasAnalysis) {
        // if no social then dont show buttons and we'll just be using news-all
        this.setSelectedSources(this.news.id);
        return null;
      }
      return this.itemSources;
    },
    sourceSelectedTitle() {
      if (this.selectedSources && this.sourceItems) {
        const SELECTED = lodashFind(this.sourceItems, [
          "value",
          this.selectedSources,
        ]);
        if (SELECTED) {
          return SELECTED.text;
        }
      }
      return null;
    },
    labelColorSelectedTitle() {
      if (this.labelColorSelected && this.labelColorSelected.name) {
        return this.labelColorSelected.name;
      }
      return "All Labels";
    },
    labelColorSelectedColor() {
      if (this.labelColorSelected && this.labelColorSelected.value) {
        return this.labelColorSelected.value;
      }
      return "#607d8b";
    },
    labelColors() {
      let labelColors = lodashCloneDeep(this.labelNames);
      labelColors.unshift({
        name: "All Labels",
        value: null,
      });
      return labelColors;
    },
    sortSelectedTitle() {
      if (this.sortSelected) {
        return this.sortSelected.name;
      }
      return this.sortItems[0].name;
    },
    eventTypesShown() {
      const PRIMARY_TOPICS = getPrimaryTopics();
      const SORTED_TOPICS = lodashOrderBy(PRIMARY_TOPICS, ["order"], ["asc"]);
      let types = [];
      SORTED_TOPICS.forEach((topic) => {
        if (this.featureUnlocked(`event-${topic.type}`)) {
          types.push(topic.type);
        }
      });
      types.push("all");
      return types;
    },
  },
  mounted() {
    this.getPrefs();
  },
  methods: {
    setSelectedSources(value) {
      this.selectedSources = value;
    },
    handleChangeSort(sort) {
      this.sortSelected = sort;
      this.$emit(EMITTED_ACTIONS.CHANGE_SORT, sort);
    },
    handleSearchKeyup(evt) {
      if (evt && evt.key.toLowerCase() === "enter") {
        this.$emit(EMITTED_ACTIONS.CHANGE_SEARCH, this.search);
      }
    },
    handleSearchClick() {
      this.search = this.searchMarker ? null : this.search;
      this.$emit(EMITTED_ACTIONS.CHANGE_SEARCH, this.search);
    },
    handleChangeDate(dates) {
      this.$emit(EMITTED_ACTIONS.CHANGE_DATE, dates);
    },
    handleChangeSource(type) {
      this.setSelectedSources(type.value);
      this.$emit(EMITTED_ACTIONS.CHANGE_SOURCE, type);
    },
    handleChangeLabelColor(color) {
      this.labelColorSelected = color;
      this.$emit(EMITTED_ACTIONS.CHANGE_LABEL_COLOR, color);
    },
    handleTopicsFilterClick(items) {
      this.eventTypesSelected = items;
      this.$emit(EMITTED_ACTIONS.CHANGE_TOPICS_FILTER, items);
    },
    handleChangeRiskCategories(items) {
      this.riskCategoriesSelected = items.riskCategories;
      this.$emit(EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES_FILTER, items);
    },
    handleOpenCustomizeLabelsClick() {
      this.dialogLabels = true;
    },
    handleCustomizeLabelKeyup() {
      lodashDebounce(() => {
        this.savePrefs();
      }, 1000)();
    },
    handleCustomizeLabelsCloseClick() {
      this.dialogLabels = false;
    },
    getPrefs() {
      this.$store.dispatch(MODULE_PREFIX_USER + GET_PREFERENCES);
    },
    savePrefs() {
      let params = this.preferences;
      params.collaboration = params.collaboration || {};
      params.collaboration.labels = this.labelNames;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, params);
    },
  },
  watch: {
    searchMarker: {
      handler() {
        this.searchIcon = this.searchMarker ? "fas fa-times" : "fas fa-search";
      },
    },
    "preferences.collaboration.labels": {
      handler() {
        if (lodashGet(this.preferences, "collaboration.labels")) {
          this.labelNames = this.preferences.collaboration.labels;
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.hyp-search {
  font-size: 12px;
}
.hyp-sort-btn {
  border-radius: 5px;
}
.hyp-source-btn {
  border-radius: 5px;
}
.hyp-label-color-btn {
  border-radius: 5px;
}
.hyp-label-textbox {
  font-size: 16px;
}
</style>
